<script>
    import { onMount, tick } from "svelte";
    import { writable, derived } from "svelte/store";
    import { keyphrases } from "../../../app-store";
    import KeyphrasesClusterChart from "./keyphrases-cluster-chart.svelte";
    import { checkUrl } from "../../../services/utils";
    import { navigate } from "svelte-routing";

    export let params;
    export let draftSubmit = () => {};

    // Create a store from the params prop
    const paramsStore = writable(params);

    let showExpertNotification = false;
    let _thematic_keywords = [];
    let chart_el = null;
    let isLoadingExistingDraft = true;
    let isUnableToLoadThematicKeywords = false;

    // Update store when prop changes
    $: paramsStore.set(params);

    // Update chart when params change
    $: chartKey = `chart-${params.project_ref}-${params.draft_refcode}`;

    $: console.log("app-store: keyphrases", $keyphrases);

    const initTooltips = () => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
    }

    const onParamsChanges = async (currentParams) => {
        if (!currentParams || !currentParams.project_ref || !currentParams.draft_refcode) {
            return;
        }
        if (params.project_ref && params.draft_refcode) {
            app.body_spinner.add_to(document.body);
            // reset store
            $keyphrases = [];
            // reset thematic keywords
            _thematic_keywords = [];
            // reset chart
            if (chart_el) {
                chart_el.dispatchEvent(
                    new CustomEvent("chart:reset", {
                        detail: {},
                    })
                );
            }
            try {
                // Get draft
                const draftResponse = await app.lucep_ai.get_draft(params.draft_refcode);

                // Check if the draft has suggested_content, navigate to the article content if it does
                if (draftResponse.draft.suggested_content) {
                    navigate(
                        `/article-content/${params.project_ref}/${params.draft_refcode}`,
                        { state: { suggested_content: draftResponse.draft.suggested_content } }
                    );
                    return;
                }
                // Check if the draft has recommended_articles, navigate to the article list if it does
                if (draftResponse.draft.recommended_articles) {
                    navigate(
                        `/article-list/${params.project_ref}/${params.draft_refcode}`,
                        { state: { recommended_articles: draftResponse.draft.recommended_articles } }
                    );
                    return;
                }

                // Get thematic keywords
                const { thematic_keywords }  = await app.lucep_ai.get_thematic_keywords(params.project_ref);

                // Update the thematic keywords
                _thematic_keywords = thematic_keywords;

                // Wait for DOM to update with new keywords
                await tick();

                // Update the chart with the new thematic keywords
                const keywords = draftResponse.draft.keyphrases.map((item) => item.keyphrase);
                for (let k of keywords) {
                    if (chart_el) {
                        chart_el.dispatchEvent(
                            new CustomEvent("chart:select", {
                                detail: { keyword: k },
                            })
                        );
                    }
                }

                await tick();
                initTooltips();
            } catch (e) {
                console.error("Error loading draft or thematic keywords:", e);
                app.toast_msg.show("Error loading draft or thematic keywords: " + e.message);
                isUnableToLoadThematicKeywords = true;
            } finally {
                app.body_spinner.remove();
                isLoadingExistingDraft = false;
            }

        }
    };

    // Track changes with a derived store
    const paramsChanged = derived(
        paramsStore,
        ($params, set) => {
            set($params); // Set the current value

            return () => {
                // This function will be called on next update
                onParamsChanges($params);
            };
        },
        () => {} // Initial value
    );

    // Subscribe params
    const unsubscribeParams = paramsChanged.subscribe((changedFn) => {
        if (typeof changedFn === "function") {
            changedFn();
        }
    });

    /**
     * @param {Object} data - The data object containing the keyword information.
     * @type {Array<Object>}
     * @example
     * {
     *   "id": 535,
     *   "expert": false,
     *   "intent": {
     *     "informational": 1
     *   },
     *   "volume": "720",
     *   "keyword": "department of motor vehicles wait times",
     *   "difficulty": "55",
     *   "references": [
     *     "https://virtuaq.com/blog/2019-12-27-dmv-wait-times",
     *     "https://qless.com/industry/dmv"
     *   ],
     *   "total_traffic": 2550580,
     * }
     */
    const on_keyword_selected = (data) => {
        data.isValidating = true;
        // Only add the keyword if it's not already in the array
        if (!$keyphrases.some((item) => item.keyword === data.keyword)) {
            $keyphrases = [...$keyphrases, data];
        } else {
            // Update the existing item with isValidating flag
            $keyphrases = $keyphrases.map((item) =>
                item.keyword === data.keyword ? { ...item, isValidating: true } : item
            );
        }
        checkUrl(params.draft_refcode, data.references[0], data.keyword, {
            ok: (urlref) => {
                $keyphrases = $keyphrases.map((item) => {
                    if (item.keyword === data.keyword) {
                        return {
                            ...item,
                            urlref: urlref,
                            isValidating: false,
                            status: "valid",
                        };
                    }
                    return item;
                });
            },
            invalid: () => {
                $keyphrases = $keyphrases.map((item) => {
                    if (item.keyword === data.keyword) {
                        return {
                            ...item,
                            isValidating: false,
                            status: "invalid",
                        };
                    }
                    return item;
                });
            },
            error: () => {
                $keyphrases = $keyphrases.map((item) => {
                    if (item.keyword === data.keyword) {
                        return {
                            ...item,
                            isValidating: false,
                            status: "crawl-error",
                        };
                    }
                    return item;
                });
                app.toast_msg.show("Error validating URL");
            },
        });
    };

    const on_keyword_unselected = (card) => {
        $keyphrases = $keyphrases.filter((item) => item.keyword !== card.keyword);
    };

    const on_card_remove = (id) => {
        chart_el.dispatchEvent(
            new CustomEvent("chart:unselect", {
                detail: {
                    id: id,
                },
            })
        );
    };

    const unsubscribeKeyphrases = keyphrases.subscribe((k) => {
        initTooltips();
    })

    // Check if the keyword is expert
    $: {
        showExpertNotification = $keyphrases.some((card) => card.expert);
    }

    onMount(() => {
        return () => {
            unsubscribeParams();
            unsubscribeKeyphrases();
        }
    });
</script>

<div class="suggestion-draft">
    <div
        class="podcast-video-alert alert alert-primary d-flex align-items-center mx-3 mb-0 {showExpertNotification
            ? ''
            : 'hidden'}"
    >
        <img class="podcast-video-icon" src="/img/podcast-video.svg" alt="Podcast or Video Icon" />
        <div>
            Expert keywords selected. Adding podcast or video content will enhance credibility and engagement
            for these technical topics.
        </div>
    </div>
    <div class="keyphrases-chart-wrapper">
        <div class="keyphrases-container">
            {#if _thematic_keywords.length}
                {#key chartKey}
                    <KeyphrasesClusterChart
                        bind:chart_el
                        thematic_keywords={_thematic_keywords}
                        selected_keywords={$keyphrases.map((item) => item.keyword)}
                        {on_keyword_selected}
                        {on_keyword_unselected}
                    />
                {/key}
            {:else if isLoadingExistingDraft}
                <div class="spinner-container">
                    <span class="spinner-border spinner-border-sm me-3"></span>
                    <span>Loading chart...</span>
                </div>
            {:else if isUnableToLoadThematicKeywords}
                <div class="alert alert-danger text-center">
                    <strong>Unable to load thematic keywords.</strong>
                    <p class="mb-0">Please try again later.</p>
                </div>
            {/if}
        </div>
        <div class="chart-sidebar-wrapper">
            <div class="keyphrases-sidebar">
                {#if $keyphrases.length === 0}
                    <div class="init-msg p-3 text-center">
                        <p class="text-muted mb-2">
                            Explore the keyword clusters and click on any circle to select a keyword.
                        </p>
                    </div>
                {:else}
                    {#each $keyphrases as card}
                        <div
                            class="card keyword-card animate-in {card.status === 'valid'
                                ? 'opacity-100'
                                : 'opacity-50'}"
                        >
                            <div class="card-body p-2">
                                <button
                                    type="button"
                                    class="btn-close col-3"
                                    title="Remove"
                                    aria-label="Close"
                                    on:click={() => {
                                        on_card_remove(card.id);
                                    }}
                                >
                                </button>

                                <div
                                    class="keyword mb-0 col fw-bold fs-5 text-truncate"
                                    title={card.keyword}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    data-bs-title={card.keyword}
                                >
                                    {card.keyword}
                                </div>

                                <div class="mb-1">
                                    <small class="d-flex justify-content-between">
                                        <span class="fw-bold">Difficulty:</span>
                                        <span class="fw-bold">{card.difficulty || 0}/100</span>
                                    </small>
                                    <div class="progress">
                                        <div
                                            class="progress-bar"
                                            role="progressbar"
                                            style="width: {card.difficulty || 0}%"
                                            aria-valuenow={card.difficulty || 0}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        ></div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col border-end d-flex est_clicks">
                                        <small class="fw-bold">Est. Competitor Clicks: </small>
                                        <small class="fw-bold px-1">{card.est_clicks?.toLocaleString() ?? "?"}</small>
                                    </div>
                                    <div class="col border-start volume">
                                        <small class="fw-bold">Volume: </small>
                                        <small class="fw-bold">{card.volume}</small>
                                    </div>
                                </div>

                                <div class="d-flex align-items justify-content-between">
                                    <div class="d-flex align-items-center gap-1">
                                        {#each Object.entries(card.intent || {}) as [intentType, intentValue]}
                                            <div class="small chip {intentType}">{intentType}</div>
                                        {/each}
                                    </div>
                                    {#if card.is_expert}
                                        <span class="text-success chip text-bg-dark small text-white">Expert</span>
                                    {/if}
                                </div>
                            </div>

                            <div class="card-footer references p-2">
                                {#if card.isValidating}
                                    <div class="spinner-container">
                                        <span class="spinner-border spinner-border-sm me-3"></span>
                                        <span>Validating URL...</span>
                                    </div>
                                {:else}
                                    <a
                                        title={card.references?.[0] || ""}
                                        href={card.references?.[0] || "#"}
                                        target="_blank"
                                        class="reference-0 small text-truncate d-block"
                                    >
                                        {card.references?.[0] || ""}
                                    </a>
                                {/if}
                            </div>
                        </div>
                    {/each}
                {/if}
            </div>
        </div>
    </div>
    <div class="bottom-bar">
        <div class="bottom-bar-content">
            {#if isLoadingExistingDraft}
                <div class="spinner-container">
                    <span class="spinner-border spinner-border-sm me-3"></span>
                    <span>Loading existing draft...</span>
                </div>
            {:else}
                <button on:click={draftSubmit} type="button" class="btn btn-submit btn-primary btn-lg btn-draft-submit"
                    >Submit</button
                >
            {/if}
        </div>
    </div>
</div>

<style>
    .est_clicks {
        flex: 1 1 50%
    }
    .volume {
        flex: 1 0 30%;
    }
    .btn-submit {
        font-size: 1.25rem;
        margin-top: 0;
        justify-content: center;
    }
    .keyphrases-chart-wrapper {
        display: grid;
        padding: 1rem;
        gap: 1rem;
        /* Space with bottom bar */
        margin-bottom: 5rem;
    }
    .bottom-bar {
        left: auto;
        right: auto;
        width: 100%;
    }
    .bottom-bar-content {
        display: flex;
        justify-content: center;
    }
    @media screen and (min-width: 768px) {
        .keyphrases-chart-wrapper {
            grid-template-columns: 50% 50%;
        }
        .bottom-bar-content {
            justify-content: flex-end;
        }
    }
    @media screen and (min-width: 992px) {
        .bottom-bar {
            left: 240px;
            right: 0;
            width: auto;
        }
    }
</style>
